/**
 * Marktdaten-Provider-Resolver.
 *
 * VITE_MARKET_DATA_PROVIDER:
 * - 'null'           → Default, keine Marktdaten
 * - 'finnhub'        → Quote + Profile (ROE/KGV/MarketCap), aber kein OHLCV
 * - 'twelvedata'     → Quote + OHLCV, aber kein ROE/KGV
 * - 'hybrid'         → Finnhub für Quote/Profile, TwelveData für OHLCV (Empfehlung)
 * - 'ibkr'           → Phase 3, noch nicht implementiert
 *
 * V6-ARCH (06-05): Tiingo-Provider entfernt — wurde nie produktiv genutzt.
 * Architektur-Entscheidung: APIs (TwelveData/Finnhub/CoinGecko) für Skelett,
 * Perplexity für Tiefenrecherche.
 *
 * Wechsel = Env-Var-Änderung, kein Code-Refactor.
 */

import type { MarketDataProvider } from './types';
import { NULL_PROVIDER } from './null-provider';
import { FINNHUB_PROVIDER } from './finnhub-provider';
import { TWELVEDATA_PROVIDER } from './twelvedata-provider';
import { HYBRID_PROVIDER } from './hybrid-provider';
import { withSupabaseCache } from './cached-provider';

type ProviderName = 'null' | 'finnhub' | 'twelvedata' | 'hybrid' | 'ibkr';

function readProviderName(): ProviderName {
  const raw = import.meta.env['VITE_MARKET_DATA_PROVIDER'] as string | undefined;
  const valid: ProviderName[] = ['null', 'finnhub', 'twelvedata', 'hybrid', 'ibkr'];
  if (raw && valid.includes(raw as ProviderName)) {
    return raw as ProviderName;
  }
  return 'null';
}

let cached: MarketDataProvider | null = null;

export function getMarketDataProvider(): MarketDataProvider {
  if (cached) return cached;
  const name = readProviderName();

  // Audit KK4: Live-Provider werden mit Supabase-Cache umhüllt, sodass
  // Quote/OHLCV beim App-Start sofort aus dem Server-Cache kommen und nur
  // bei abgelaufenem TTL ein Live-Call ausgelöst wird.
  switch (name) {
    case 'finnhub':
      cached = withSupabaseCache(FINNHUB_PROVIDER);
      return cached;
    case 'twelvedata':
      cached = withSupabaseCache(TWELVEDATA_PROVIDER);
      return cached;
    case 'hybrid':
      cached = withSupabaseCache(HYBRID_PROVIDER);
      return cached;
    case 'ibkr':
      // eslint-disable-next-line no-console
      console.warn(
        `[market-data] Provider 'ibkr' ist konfiguriert, aber noch nicht implementiert (Phase 3). Fallback auf Hybrid.`
      );
      cached = withSupabaseCache(HYBRID_PROVIDER);
      return cached;
    case 'null':
    default:
      cached = NULL_PROVIDER;
      return cached;
  }
}

export type { MarketDataProvider, SymbolLookupResult, PriceQuote, OhlcvBar } from './types';
